import * as React from 'react';
import classnames from 'classnames';
import { Button } from '@msdyn365-commerce-modules/utilities';
import {
    ProductInput,
    GetCustomerInput, getCustomer, buildWishlistLine, getWishlistByCustomerId
} from '@msdyn365-commerce-modules/retail-actions';
import { IProductListCompactComponentProps } from '../product-list-compact-component';
import { ProductListProduct } from '../../product-list-component/product-list-component';
import { addLinesAsync, createCommerceListAsync, createGetByCustomerInput, removeLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CommerceListsDataActions.g';
import { CommerceList } from '@msdyn365-commerce/retail-proxy';
import { toast, toasts } from '../../toast/toast';
import { getLocale } from '../../product-list-component/locale';

export interface IWishlistButtonProps extends IProductListCompactComponentProps {
    product: ProductListProduct;
    locale: ReturnType<typeof getLocale>;
    onClick?: () => void;
}

const WishlistButton = (props: IWishlistButtonProps) => {
    const {
        context,
        resources,
        locale,
        product,
        wishlists
    } = props;

    const isWishlisted = (wishlists?.length || 0) > 0 && wishlists![0].CommerceListLines?.some(x => x.ProductId === product.RecordId);
    const buttonText = isWishlisted
            ? resources.removeFromWishlistText || 'Fjern fra huskelisten'
            : resources.addToWishlistText || 'Legg til i huskelisten';

    const callAddWishlistLine = async (productId: number, wishlistId: number) => {
        try {
            const customer = await getCustomer(new GetCustomerInput(context.request.apiSettings), context.actionContext);
            if (customer && customer.AccountNumber) {
                const createdCommerceList = await addLinesAsync({ callerContext: context.actionContext },
                    wishlistId,
                    [
                        buildWishlistLine(productId, customer.AccountNumber, wishlistId)
                    ]);
                context.actionContext.update(createGetByCustomerInput({}, null), [createdCommerceList]);
            }
        } catch (err) {
            const error = err as unknown as any;
            const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
            toasts.error(locale.toastStrings.errorHeader, error, { footer });
        }
    };

    const moveItemToWishlist = async () => {
        props.onClick?.();

        if (!context || !product || !context.request.user.catalogId) {
            return;
        }

        const productInputs: ProductInput[] = [];
        const productInput = new ProductInput(
            product.RecordId!,
            context.request.apiSettings,
            undefined, undefined,
            context.request,
            +context.request.user.catalogId
        );
        productInputs.push(productInput);

        if (context.request.user.isAuthenticated) {
            const customer = await getCustomer(new GetCustomerInput(context.request.apiSettings), context.actionContext);

            let wishlists = props.wishlists;

            if (!wishlists) {
                wishlists = await getWishlistByCustomerId(createGetByCustomerInput({}, null), context.actionContext);
            }

            if (!wishlists || wishlists.length === 0) {
                const wishlistName = props.defaultWishlistName || 'My wishlist';
                if (customer && customer.AccountNumber) {
                    const listData: CommerceList = {
                        CustomerId: customer.AccountNumber,
                        Name: wishlistName,
                        CommerceListTypeValue: 1,
                        Id: 0
                    };
                    try {
                        const commerceList = await createCommerceListAsync({
                                callerContext: context.actionContext
                            },
                            listData);
                        await callAddWishlistLine(product.RecordId!, commerceList.Id);

                        toast({
                            text: `${product.Name} lagt i huskelisten`,
                            type: 'success',
                            autoClose: 3000,
                            position: 'bottom-right',
                            canClose: true,
                            showProgress: false
                        });
                    } catch (err) {
                        const error = err as unknown as any;
                        const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
                        toasts.error(locale.toastStrings.errorHeader, error, { footer });
                    }
                }
            } else {
                await callAddWishlistLine(product.RecordId!, wishlists[0].Id);

                toast({
                    text: `${product.Name} lagt i huskelisten`,
                    type: 'success',
                    autoClose: 3000,
                    position: 'bottom-right',
                    canClose: true,
                    showProgress: false
                });
            }
        }
    };

    const removeItemFromWishlist = async () => {
        props.onClick?.();

        if (!context || !product || !context.request.user.catalogId || !context.request.user.isAuthenticated) {
            return;
        }

        let wishlists = props.wishlists;

        if (!wishlists) {
            wishlists = await getWishlistByCustomerId(createGetByCustomerInput({}, null), context.actionContext);
        }

        if (wishlists.length > 0) {
            const commerceLineToRemove = wishlists[0].CommerceListLines?.find(x => x.ProductId === product.RecordId);
            const updatedCommerceList = await removeLinesAsync({ callerContext: context.actionContext },
                wishlists[0].Id,
                [commerceLineToRemove!]);
            context.actionContext.update(createGetByCustomerInput({}, null), [updatedCommerceList]);

            toast({
                text: `${product.Name} fjernet fra huskelisten`,
                type: "success",
                autoClose: 3000,
                position: "bottom-right",
                canClose: true,
                showProgress: false
            });
        }
    };

    return (
        <Button
            className={classnames('btn-add-to-wishlist', { 'wishlisted': isWishlisted })}
            title={buttonText}
            aria-label={buttonText}
            onClick={isWishlisted ? removeItemFromWishlist : moveItemToWishlist}
        >
            {buttonText}
        </Button>
    );
};

export default WishlistButton;
